import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildTranscriptSearchIndex } from 'src/components/Assistant/live-transcript/live-transcript.utils';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';
import { type CallTranscriptSegment } from './call-transcript.utils';

const SEARCH_DEBOUNCE_MS = 150;

export const useCallTranscriptSearch = (segments: CallTranscriptSegment[]) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { totalMatches, matchOffsetBySegmentId } = useMemo(
    () => buildTranscriptSearchIndex(segments, debouncedSearchQuery),
    [segments, debouncedSearchQuery],
  );
  const effectiveMatchIndex =
    totalMatches === 0 ? 0 : Math.min(currentMatchIndex, totalMatches - 1);

  useEffect(() => {
    const container = scrollRef.current;
    if (!isSearchOpen || !debouncedSearchQuery || totalMatches === 0 || !container) {
      return;
    }
    const target = container.querySelector<HTMLElement>(
      `[data-match-index="${effectiveMatchIndex}"]`,
    );
    if (!target) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset =
      targetRect.top - containerRect.top - container.clientHeight / 2 + targetRect.height / 2;
    container.scrollBy({ top: offset, behavior: 'smooth' });
  }, [effectiveMatchIndex, isSearchOpen, debouncedSearchQuery, totalMatches]);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setCurrentMatchIndex(0);
  }, []);

  const onSearchQueryChange = useCallback((next: string) => {
    setSearchQuery(next);
    setCurrentMatchIndex(0);
  }, []);

  const prevMatch = useCallback(() => {
    if (totalMatches === 0) return;
    setCurrentMatchIndex((current) => {
      const effective = Math.min(current, totalMatches - 1);
      return (effective - 1 + totalMatches) % totalMatches;
    });
  }, [totalMatches]);

  const nextMatch = useCallback(() => {
    if (totalMatches === 0) return;
    setCurrentMatchIndex((current) => {
      const effective = Math.min(current, totalMatches - 1);
      return (effective + 1) % totalMatches;
    });
  }, [totalMatches]);

  return {
    isSearchOpen,
    openSearch,
    closeSearch,
    searchQuery,
    debouncedSearchQuery,
    onSearchQueryChange,
    totalMatches,
    matchOffsetBySegmentId,
    currentMatchIndex: effectiveMatchIndex,
    prevMatch,
    nextMatch,
    scrollRef,
  };
};
